import type { inferRouterInputs, inferRouterOutputs } from '@trpc/server'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { toast } from 'react-hot-toast'
import type { AppRouter } from '@/api/_trpc/router'
import type { SubmissionKind } from '@/db/schema/enums'
import { commentBodyResolver } from '@/lib/coaching/review-comment-body'
import { isSavableComment } from '@/lib/coaching/review-comment-visibility'
import { trpc } from '@/lib/trpc'
import { errorReason } from '../shared/trpc-error'
import type { CommentKind, KindCounts, TypeCounts } from '../store'
import {
  countByKind,
  countByQuestionType,
  emptyKindCounts,
  emptyTypeCounts,
  formatRelativeWhen,
  sortByWorkedAt,
  surfacedError,
  toPracticeRow,
  toReviewComments,
  toReviewPlanWeek,
  toReviewSubmission,
  toTargetKey,
} from './_lib/live-review'
import type {
  ReviewApproach,
  ReviewComment,
  ReviewPlanWeek,
  ReviewSubmission,
  ReviewWeekGroup,
} from './contracts'
import {
  type ComposerState,
  type OverallDraft,
  useReviewUiState,
  useWeekFolds,
} from './review-ui-state'

/**
 * Review slice — THE cross-lane interface (spec §6, §8, §11). `store.tsx`
 * imports `ReviewSlice` from this file and never edits the contract itself.
 *
 * LG-503 replaced the mock backing with the shipped `coachingV2.reviews.*`
 * surface, and LG-646 flipped which reads it uses: `listWeeks` gives the plan
 * weeks the tab sections by, `listWeekWork` gives one week's rows when the
 * coach opens it, and `listComments` follows the rows that arrive. The
 * lifecycle/comment actions map onto `claim`, `finish`, `reopen`,
 * `createComment`, `approveComment`, `dismissComment` and `postComment`. The fixtures that used to back this hook now live behind
 * `mock-review-slice.tsx`, which the mock-only shell provider keeps using.
 *
 * The AI-draft approval actions still exist because behavioral and
 * system-design reviews keep their pending-comment lifecycle; LG-632's cut is
 * coding-only, enforced where coding is knowable — the mapper suppresses
 * pending AI rows on coding submissions (`toReviewComments`) and the coding
 * seeds carry none — never by removing the shared controls.
 *
 * Coach comments do not share that lifecycle. They are written as drafts and
 * released by `publish`/`unpublish`, review at a time (LG-641), on every
 * review type alike — the visibility rule itself is untouched, so what
 * behavioral and system-design reviews show the client is unchanged.
 *
 * The slice never encodes what a lifecycle command does beyond the review
 * itself. For coding work `finish` only flips the reviewed flag — the
 * assignment completed the moment the work qualified (LG-635) — while
 * behavioral and system-design work still completes on the coach's finish.
 * Either way this file renders what the queue returns afterwards.
 */
export interface ReviewSlice {
  feedbackMix: { thisWeek: KindCounts; lastWeek: KindCounts }
  byQuestionType: { thisWeek: TypeCounts; lastWeek: TypeCounts }
  awaitingCount: number

  /**
   * The tab's spine (LG-646): every plan week from the engagement start
   * through the current one, newest first, each with the rows read for it.
   *
   * Sections come from the plan rather than from the review queue, so a week
   * the client was quiet in reads as quiet instead of as absent, and each row
   * sits under the week it was *worked* in (ADR 0013). Rows arrive a week at a
   * time, when the coach opens that week — `queue` below is whatever that adds
   * up to right now, not the client's whole history.
   */
  weeks: ReviewWeekGroup[]
  /**
   * Every row currently loaded, across the open weeks. What the derived
   * counters and the per-submission lookups are defined over; the week
   * sections themselves render from `weeks` above.
   */
  queue: ReviewSubmission[]
  expandedId: string | null
  setExpandedId: (id: string | null) => void
  /** Whether a week section is folded away — see `WeekFoldState`. */
  isWeekCollapsed: (weekKey: string) => boolean
  /** Folds or unfolds a week section — see `WeekFoldState`. */
  toggleWeekCollapsed: (weekKey: string) => void

  /** ALL comments across submissions, single flat array (spec §8.8). */
  comments: ReviewComment[]
  /** Non-dismissed comments belonging to one submission. */
  commentsFor: (submissionId: string) => ReviewComment[]
  /**
   * The approaches authored for one submission's problem (LG-640), in the
   * catalog's own `(order, id)` order. Empty for a submission whose problem
   * has none, whose card is not the expanded one, and while the read is in
   * flight — the section renders nothing in all three cases rather than an
   * empty picker.
   */
  approachesFor: (submissionId: string) => ReviewApproach[]
  /**
   * Why this submission's approaches could not be read, or null. Separate from
   * the lane's `status.error`, which the queue renders by blanking itself: a
   * failed approach read must not take the queue down, but it must not pass
   * for "this problem has no alternatives" either.
   */
  approachesErrorFor: (submissionId: string) => string | null
  approve: (id: string) => void
  dismiss: (id: string) => void
  /** body ← alt (canned rewrite). */
  rewrite: (id: string) => void
  editBody: (id: string, body: string) => void
  /**
   * Saves the typed-over body of one comment through the revision machinery:
   * a `coach_edit` revision, published in the same transaction, so a comment
   * the client is already reading is corrected with its history intact
   * (LG-641). Same never-rejects contract as the commands below: a failure is
   * a toast, and the unsaved text stays on screen for another try.
   */
  saveEdit: (id: string) => Promise<void>
  /**
   * Appends {source: 'coach', status: 'pending'} — a draft — to the expanded
   * submission. Nothing the coach writes reaches the client until `publish`.
   */
  post: (c: Omit<ReviewComment, 'id' | 'status' | 'source'>) => void
  /**
   * Approved → posted (spec §8.2): stamps `postedAt` on an approved comment.
   * The client already reads approved comments, so this is the coach's
   * explicit sign-off mark on one comment, not a visibility gate.
   */
  markPosted: (id: string) => void

  /**
   * Lifecycle commands. Each resolves once the write has been attempted AND
   * the queue re-read — never rejects: a failure is reported to the coach as a
   * toast and shows up as the unchanged status the queue comes back with. Do
   * not read "resolved" as "it landed"; sequence UI off the refreshed queue.
   */
  claim: (reviewId: string) => Promise<void>
  /**
   * Marks the review done. What else that does — nothing at all for coding
   * work, completing the assignment for the review-gated kinds — is the
   * service's business, not this slice's.
   */
  finish: (reviewId: string) => Promise<void>
  /** Puts a finished review back in front of the coach. */
  reopen: (reviewId: string) => Promise<void>
  /**
   * Pulls one unassigned practice session into the week it was worked in
   * (LG-647), so the coach has something to comment on: the assignment is
   * created after the fact and the work is run through the same qualifying
   * path the producer uses. Takes the session id — a practice row's own id —
   * and resolves the week from the section the row was read in, which is the
   * only week the server accepts. Same never-rejects contract as the
   * lifecycle commands above.
   */
  pull: (sessionId: string) => Promise<void>

  /**
   * Publication, on the same never-rejects contract as the lifecycle commands
   * above. `publish` releases this review's coach drafts to the client and is
   * the ONLY thing that does — `finish` is a queue-state action and publishes
   * nothing (LG-641).
   */
  publish: (reviewId: string) => Promise<void>
  /**
   * Takes the released comments back, once `confirm` says so.
   *
   * The question belongs to the caller, the evidence behind it does not:
   * `confirm` is handed the client's last view of this feedback, in the
   * queue's relative phrasing, and that same reading is what the write
   * acknowledges. `null` is "not since this was published" — not "never" —
   * and `undefined` is "nothing here tracks it", as on a fixture-backed row.
   *
   * Asked again, with a newer reading, when the client opens the feedback
   * between the two: the service refuses to withdraw on evidence that has
   * moved, so a coach who was told nobody had read it is never the coach who
   * silently took it back from somebody who had.
   */
  unpublish: (
    reviewId: string,
    confirm: (clientLastViewedWhen: string | null | undefined) => boolean,
  ) => Promise<void>

  composer: ComposerState | null
  openComposer: (
    section: string,
    target: string,
    defaultKind: CommentKind,
    targetKey?: string,
  ) => void
  /** Opens a code-line composer, building the label off the anchor itself. */
  openLineComposer: (
    section: string,
    labelPrefix: string,
    defaultKind: CommentKind,
    lines: [number, number],
  ) => void
  closeComposer: () => void
  /** Extends the open code composer's line anchor, and its label, into a range. */
  extendComposerLines: (line: number) => void
  /** Switches the open composer's kind without ending the composing session. */
  setComposerKind: (kind: CommentKind) => void
  /** Records a keystroke in the open composer — see `ComposerState.draft`. */
  setComposerDraft: (draft: string) => void

  /**
   * Overall-comment drafts by submission id; absent means untouched. On the
   * slice rather than in the block because a week fold unmounts the expanded
   * card the block renders in (LG-642). Keyed by submission and kept across a
   * submission switch — see `ReviewUiState` for why the composer is not.
   */
  overallDrafts: Partial<Record<string, OverallDraft>>
  setOverallDraft: (submissionId: string, draft: OverallDraft) => void

  /**
   * The catalog approaches this submission's review currently has attached
   * (LG-644, spec D22) — persisted `ReviewAttachment` rows of kind
   * `approach`, not a local flag. Empty for any submission that is not the
   * expanded one, for the same reason `approachesFor` is: the read is scoped
   * to the card on screen.
   *
   * `null` when the expanded card's read has not answered — still loading, or
   * failed. Distinct from the empty set on purpose: an empty set is the claim
   * "nothing is attached", and making a failed read tell that lie would both
   * mislabel every chip and send the next click down the attach branch for an
   * approach that already has a live row.
   */
  attachedApproachIdsFor: (submissionId: string) => ReadonlySet<string> | null
  /**
   * Why the attachment read failed, when it did and there is nothing cached to
   * contradict it. Mirrors `approachesErrorFor`.
   */
  attachmentsErrorFor: (submissionId: string) => string | null
  /**
   * Attach or detach one catalog approach on this submission's review. Which
   * of the two it is follows from what is attached now, so the caller passes
   * the approach and not an intent.
   *
   * Returns the write's promise, like `claim` / `finish` / `reopen`, so the
   * caller can hold the control inert until it settles — a second click while
   * a detach is in flight would otherwise re-send it and be refused for a
   * detach that actually succeeded.
   */
  toggleApproachAttachment: (
    submissionId: string,
    approach: ReviewApproach,
  ) => Promise<void>

  /**
   * Local, unpersisted "attach to review" toggles for the star-answer,
   * HLD-diagram, deep-dives, alternative-stories and suggested-resources
   * sections — behavioral and system-design surfaces, out of scope for the
   * coding review project. The alternative-*approach* toggle used to live
   * here too and no longer does (LG-644): it is the pair above, so nothing
   * this map holds can move without one of those five sections asking.
   *
   * `Partial<>` because it only ever holds keys that were toggled — the same
   * correction LG-642 made to the week folds. Without it the type claims
   * every string key is present and the `?? false` every consumer writes
   * reads as dead code.
   */
  attachments: Partial<Record<string, boolean>>
  toggleAttachment: (key: string) => void

  /** Load state of the backing reads. Fixture-backed slices report ready. */
  status: { isLoading: boolean; error: string | null }
}

type RouterInputs = inferRouterInputs<AppRouter>
type RouterOutputs = inferRouterOutputs<AppRouter>
type Inputs = RouterInputs['admin']['coachingV2']['reviews']
type Outputs = RouterOutputs['admin']['coachingV2']['reviews']

/**
 * The identity mapping, today — kept applied uniformly rather than removed
 * piecemeal, and documented as what it is.
 *
 * The premise it was written on is false: `inferRouterOutputs` already yields
 * the *serialized* shape when no transformer is configured, which is what
 * `RouterOutputs` in `lib/trpc.ts` says on its own tin ("Dates are ISO strings
 * client-side"). A `Date` in a DTO therefore arrives here already typed as
 * `string`, so `[T[K]] extends [Date]` matches nothing and every alias below
 * is structurally the raw output type. Compile-verified, not assumed: a `Date`
 * is not assignable to `Outputs['listWeekWork']['items'][number]['workedAt']`.
 *
 * Which means this defends nothing, and the `as unknown as` helpers written to
 * bridge it are unchecked seams between two identical types — the real cost,
 * since their own comments explain why a bare `as` would be wrong. Removing
 * both is tracked separately rather than folded into the week-spine change.
 *
 * The mapping is left in place meanwhile because it is inert: it cannot change
 * a type it never matches, and applying it to some aliases and not others is
 * how the inconsistency read as a defect in the first place.
 */
type WireDates<T> = {
  [K in keyof T]: [T[K]] extends [Date]
    ? string
    : [T[K]] extends [Date | null]
      ? string | null
      : T[K]
}

/**
 * One Review-tab row as it lands in the browser: a review, or a session
 * nobody submitted (LG-646). `WireDates` distributes over the union, so each
 * member's own top-level timestamps are corrected.
 */
export type ReviewWorkItem = WireDates<Outputs['listWeekWork']['items'][number]>
export type ReviewQueueItem = Extract<ReviewWorkItem, { rowKind: 'review' }>
export type PracticeSessionItem = Extract<
  ReviewWorkItem,
  { rowKind: 'session' }
>
/** One spine row as it lands in the browser. */
export type ReviewWeekRow = WireDates<Outputs['listWeeks']['weeks'][number]>

/**
 * One week's rows, plus whether the read hit its cap.
 *
 * `truncated` travels with the rows rather than beside them because it is a
 * fact about *this* read: a week whose list is capped has to say so, and the
 * only place that is knowable is where the row count met the limit.
 */
export interface ReviewWeekWork {
  items: ReviewWorkItem[]
  truncated: boolean
}
export type ReviewCommentsPage = WireDates<Outputs['listComments']>
export type ReviewAttachmentRow = WireDates<Outputs['listAttachments'][number]>

type Command<Name extends keyof Inputs> = (
  input: Inputs[Name],
) => Promise<Outputs[Name]>

export interface QueryState<T> {
  data: T | undefined
  isLoading: boolean
  error: unknown
}

/**
 * Everything the slice needs from the server, behind one injectable seam —
 * the same shape `CurriculumPersistenceApi` uses, and for the same reason:
 * the mapping is what deserves a test, not tRPC's hooks.
 */
export interface ReviewPersistenceApi {
  clientId: string
  /** null until the engagement resolves; every write is scoped by it. */
  engagementId: string | null
  /**
   * The plan weeks the tab sections by, newest first, with each week's counts
   * (LG-646). One read, up front: the counts are server-computed over the
   * whole engagement, so a week's header is honest about what it holds before
   * anyone opens it.
   *
   * `error` carries only failures worth an alert: the implementation folds
   * the weeks and engagement reads together and suppresses each against its
   * own cache first (`surfacedError`), so consumers surface whatever arrives
   * rather than second-guessing it against `data`.
   */
  weeks: QueryState<ReviewWeekRow[]>
  /**
   * `listWeekWork` for the weeks the coach currently has open, keyed by week
   * id — a hook, like `useApproaches`, because which weeks those are is UI
   * state the slice owns and this layer cannot see. Passing them back down is
   * what keeps a client's whole history off the wire: opening the tab reads
   * the spine plus one week.
   *
   * `Partial`, and the same argument `attachments` makes above: the record is
   * built over the *open* weeks, so every folded week — most of them — is
   * absent from it by construction. Without it the type claims every week id
   * is present and the `?.`/`??` every consumer writes reads as dead code
   * ripe for tidying away, which is how folding a week starts throwing.
   */
  useWeekWork: (
    weekIds: readonly string[],
  ) => Partial<Record<string, QueryState<ReviewWeekWork>>>
  /**
   * `listComments` fanned out over the loaded rows, keyed by review id. A hook
   * for the same reason `useWeekWork` is: the rows it follows are themselves
   * read per open week, so nothing above the slice knows what they are.
   *
   * `Partial` inside the `QueryState` for the same reason again — a review
   * whose page has not landed yet has no key here.
   */
  useComments: (
    reviewIds: readonly string[],
  ) => QueryState<Partial<Record<string, ReviewCommentsPage>>>
  /**
   * `listApproaches` for one review (LG-640) — a hook, like `useWeekWork` and
   * `useComments` above, because it is scoped to the *expanded* card and this
   * layer cannot know which that is: `expandedId` is local UI
   * state owned by the slice, which is built from this api rather than the
   * other way round. Passing it back down as an argument is what keeps the
   * read off the other 49 rows a queue page can hold; a `listComments`-style
   * fan-out would pull every queued problem's full solution code to render
   * one card's picker.
   *
   * Called unconditionally at the top of `useReviewSlice`, with `null`
   * standing for "nothing expanded" — the argument varies, the call does not.
   */
  useApproaches: (reviewId: string | null) => QueryState<ReviewApproach[]>
  /**
   * `listAttachments` for one review (LG-644) — a hook for the same reason
   * `useApproaches` is one, and gated the same way: the attach state belongs
   * to the card the coach has open, and fanning it out over the queue would
   * buy nothing but 49 extra joins.
   */
  useAttachments: (reviewId: string | null) => QueryState<ReviewAttachmentRow[]>
  claim: Command<'claim'>
  finish: Command<'finish'>
  reopen: Command<'reopen'>
  /** Creates the retroactive assignment behind a practice row (LG-647). */
  pullIntoWeek: Command<'pullIntoWeek'>
  createComment: Command<'createComment'>
  approveComment: Command<'approveComment'>
  dismissComment: Command<'dismissComment'>
  /**
   * Approved → posted, driven by the comment card's "Post to client" action
   * (`markPosted`). Visibility does not change — an approved comment is
   * already what the client reads (`clientVisibleComments`,
   * `lib/coaching/review-service.ts`); the service stamps `postedAt` and the
   * status becomes the coach's per-comment sign-off mark.
   */
  postComment: Command<'postComment'>
  /** Attaches one subject to a review; `removeAttachment` is its undo. */
  addAttachment: Command<'addAttachment'>
  /**
   * Detaches by stamping `removedAt`. Nothing hard-deletes, so a detach is a
   * fact about a moment the coach can be asked about later, not an erasure.
   */
  removeAttachment: Command<'removeAttachment'>
  /** Releases this review's coach drafts / takes them back (LG-641). */
  publishComments: Command<'publishComments'>
  /**
   * Answers with the client-read evidence it saw under the write when that
   * evidence has moved, so its one `Date` is corrected here — the seam every
   * other wire timestamp is corrected at — rather than at the call site.
   */
  unpublishComments: (
    input: Inputs['unpublishComments'],
  ) => Promise<WireDates<Outputs['unpublishComments']>>
  /** One saved edit: a revision written and published in one transaction. */
  editComment: Command<'editComment'>
  /** Re-reads the queue, its comments, its attachments and the counters. */
  refresh: () => Promise<void>
}

export type UseReviewPersistenceApi = (
  clientId: string,
  options: { enabled: boolean },
) => ReviewPersistenceApi

// Finish signs off on everything the coach is looking at, so the live slice
// only offers it when that everything is actually on screen. Each hold is the
// exact text the lifecycle footer renders beside the disabled button.
const COMMENTS_PENDING_REASON =
  'This review’s comments haven’t loaded yet — Finish stays off until they do.'
// LG-517 renders the client's real code, which retires this hold for coding
// rows only. The other two bodies still render the spec §8.5 seeds and not the
// client's submission — `SystemDesignReview`'s URL-shortener requirements and
// high-level design, and the behavioral body's STAR beats and alternative
// stories — so signing off there is still signing off on content the coach
// never saw. Each successor deletes its own arm of `SEED_BODY_KINDS`.
const SEED_ARTIFACT_REASON =
  'The client’s actual submission isn’t rendered here yet — Finish stays off until it is.'
// An allowlist, not a denylist: `submissionType` renders an unrecognized kind
// with the coding body, which would then offer Finish over a body that read no
// artifact at all. Naming the kind that is known-good fails closed instead.
//
// Total over `SubmissionKind` rather than a set of the true ones (LG-905). A
// set is what let `manual` arrive and silently inherit the hold: a coach could
// claim a ticked card's review and never close it, which is the exact failure
// the server-side gate was moved off `completionMode` to remove. A fifth kind
// is now a compile error here. The `string` annotation keeps the lookup honest
// against a kind a browser tab open across a deploy has never heard of.
const RENDERS_REAL_ARTIFACT: Readonly<Record<string, boolean | undefined>> = {
  coding: true,
  // A ticked card has no artifact to render, so `TaskReview` rendering none
  // *is* the whole submission — there is nothing the coach has not seen.
  manual: true,
  system_design: false,
  behavioral: false,
} satisfies Record<SubmissionKind, boolean>

function rendersRealArtifact(kind: string): boolean {
  return RENDERS_REAL_ARTIFACT[kind] === true
}

const EMPTY_ITEMS: ReviewWorkItem[] = []
const EMPTY_WEEK_ROWS: ReviewWeekRow[] = []
const EMPTY_SUBMISSIONS: ReviewSubmission[] = []
const EMPTY_PAGES: Partial<Record<string, ReviewCommentsPage>> = {}
const EMPTY_COMMENTS: ReviewComment[] = []
const EMPTY_APPROACHES: ReviewApproach[] = []
const EMPTY_ATTACHMENTS: ReviewAttachmentRow[] = []
const NO_ATTACHED_APPROACHES: ReadonlySet<string> = new Set()

// Stands in for the review id `listApproaches` is keyed by while nothing is
// expanded. The query is `enabled: false` in that state so it never leaves the
// browser — the same placeholder-plus-gate shape `persisted-curriculum.tsx`
// uses for its engagement-keyed calendar read.
const NO_REVIEW_ID = '00000000-0000-4000-8000-000000000000'

/**
 * Identity-stable view of a derived array: the reference only changes when a
 * member does. React Query keeps each page's `data` reference stable through
 * structural sharing, so this is what lets the comment fan-out participate in
 * `useMemo`/effect deps at all — a hook dependency array cannot itself change
 * length between renders.
 */
function useStableArray<T>(values: readonly T[]): readonly T[] {
  const cache = useRef<readonly T[]>(values)
  const current = cache.current
  if (
    current.length !== values.length ||
    values.some((value, index) => value !== current[index])
  ) {
    cache.current = values
  }
  return cache.current
}

/**
 * The single place the queue DTO's Date-typed timestamps are renamed to the
 * ISO strings the browser is actually handed. Everything downstream — mappers,
 * fixtures, the exported `ReviewQueueItem` — reads the corrected shape.
 */
function wireItems(
  items: Outputs['listWeekWork']['items'] | undefined,
): ReviewWorkItem[] | undefined {
  return items as unknown as ReviewWorkItem[] | undefined
}

/** The same correction for the one mutation that answers with a timestamp. */
function wireUnpublish(
  mutate: Command<'unpublishComments'>,
): ReviewPersistenceApi['unpublishComments'] {
  return mutate as unknown as ReviewPersistenceApi['unpublishComments']
}

/**
 * The attachment DTO's `attachedAt` is a `Date` on the server and an ISO
 * string by the time it reaches the browser. Named and `as unknown as`-ed like
 * `wireItems` above rather than corrected inline: a bare `as` at the call site
 * reads as a checked conversion, and would go on silently absorbing every
 * later change to the DTO at the one seam where the wire shape is set.
 */
function wireAttachments(
  rows: Outputs['listAttachments'] | undefined,
): ReviewAttachmentRow[] | undefined {
  return rows as unknown as ReviewAttachmentRow[] | undefined
}

/**
 * `enabled` follows whether the coach is actually on the Review tab. The lane
 * stays *mounted* either way — remounting it would tear down the shell rows it
 * wraps — so the reads, not the provider, are what the tab switch gates.
 */
export function useTrpcReviewPersistenceApi(
  clientId: string,
  options: { enabled: boolean },
): ReviewPersistenceApi {
  const { enabled } = options
  const utils = trpc.useUtils()
  // The engagement every write is scoped by. `overview.get` is already mounted
  // by the dashboard shell for this same client, so React Query answers this
  // from the shared cache entry rather than issuing a second request.
  const overviewQuery = trpc.admin.coachingV2.overview.get.useQuery(
    { clientId },
    { enabled },
  )
  const weeksQuery = trpc.admin.coachingV2.reviews.listWeeks.useQuery(
    { clientId },
    { enabled },
  )

  // Declared inline rather than via `useCallback`: each of these calls a hook,
  // so it must run in its caller's render — memoizing would only stabilize a
  // reference nothing depends on.
  const useWeekWork = (
    weekIds: readonly string[],
  ): Partial<Record<string, QueryState<ReviewWeekWork>>> => {
    // One request per open week, collapsed into a single HTTP call by the
    // batch link. `useQueries` takes a varying-length list, which is what lets
    // the read follow the folds instead of the whole spine.
    const stableIds = useStableArray(weekIds)
    const queries = trpc.useQueries((t) =>
      stableIds.map((weekId) =>
        t.admin.coachingV2.reviews.listWeekWork(
          { clientId, weekId },
          { enabled },
        ),
      ),
    )
    const datas = useStableArray(queries.map((query) => query.data))
    const loadings = useStableArray(queries.map((query) => query.isLoading))
    const errors = useStableArray(queries.map((query) => query.error))
    return useMemo(() => {
      const byWeek: Partial<Record<string, QueryState<ReviewWeekWork>>> = {}
      stableIds.forEach((weekId, index) => {
        const result = datas[index]
        byWeek[weekId] = {
          data:
            result === undefined
              ? undefined
              : {
                  items: wireItems(result.items) ?? EMPTY_ITEMS,
                  truncated: result.truncated,
                },
          isLoading: loadings[index] === true,
          error: errors[index],
        }
      })
      return byWeek
    }, [datas, errors, loadings, stableIds])
  }

  const useComments = (
    reviewIds: readonly string[],
  ): QueryState<Partial<Record<string, ReviewCommentsPage>>> => {
    // Comments for every loaded row — not just the expanded card — because the
    // derived counters are defined over all of them (spec §8.8).
    const stableIds = useStableArray(reviewIds)
    const queries = trpc.useQueries((t) =>
      stableIds.map((reviewId) =>
        t.admin.coachingV2.reviews.listComments({ reviewId }, { enabled }),
      ),
    )
    const pages = useStableArray(queries.map((query) => query.data))
    const isLoading = queries.some((query) => query.isLoading)
    const error = queries.find((query) => query.error)?.error
    const data = useMemo(() => {
      // Partial, and honestly so: a review whose page has not arrived has no
      // key here, and a total `Record` would let a caller index it unchecked.
      const byReview: Partial<Record<string, ReviewCommentsPage>> = {}
      stableIds.forEach((reviewId, index) => {
        const page = pages[index]
        if (page !== undefined) byReview[reviewId] = page
      })
      return byReview
    }, [pages, stableIds])
    return { data, isLoading, error }
  }

  // Declared inline rather than via `useCallback`: it calls a hook, so it must
  // run in its caller's render — memoizing it would only stabilize a reference
  // nothing depends on.
  const useApproaches = (
    reviewId: string | null,
  ): QueryState<ReviewApproach[]> => {
    const approachQuery = trpc.admin.coachingV2.reviews.listApproaches.useQuery(
      { reviewId: reviewId ?? NO_REVIEW_ID },
      { enabled: enabled && reviewId !== null },
    )
    if (reviewId === null) {
      return { data: EMPTY_APPROACHES, isLoading: false, error: null }
    }
    return {
      data: approachQuery.data,
      isLoading: approachQuery.isLoading,
      error: approachQuery.error,
    }
  }

  // Same placeholder-and-gate shape as `useApproaches`, and scoped to the
  // client as well as the review: the procedure refuses a review that is not
  // this client's, so passing the id the slice was built with is what makes
  // that refusal reachable rather than decorative.
  const useAttachments = (
    reviewId: string | null,
  ): QueryState<ReviewAttachmentRow[]> => {
    const attachmentQuery =
      trpc.admin.coachingV2.reviews.listAttachments.useQuery(
        { clientId, reviewId: reviewId ?? NO_REVIEW_ID },
        { enabled: enabled && reviewId !== null },
      )
    if (reviewId === null) {
      return { data: EMPTY_ATTACHMENTS, isLoading: false, error: null }
    }
    return {
      data: wireAttachments(attachmentQuery.data),
      isLoading: attachmentQuery.isLoading,
      error: attachmentQuery.error,
    }
  }

  const claim = trpc.admin.coachingV2.reviews.claim.useMutation()
  const finish = trpc.admin.coachingV2.reviews.finish.useMutation()
  const reopen = trpc.admin.coachingV2.reviews.reopen.useMutation()
  const pullIntoWeek = trpc.admin.coachingV2.reviews.pullIntoWeek.useMutation()
  const createComment =
    trpc.admin.coachingV2.reviews.createComment.useMutation()
  const approveComment =
    trpc.admin.coachingV2.reviews.approveComment.useMutation()
  const dismissComment =
    trpc.admin.coachingV2.reviews.dismissComment.useMutation()
  const postComment = trpc.admin.coachingV2.reviews.postComment.useMutation()
  const addAttachment =
    trpc.admin.coachingV2.reviews.addAttachment.useMutation()
  const removeAttachment =
    trpc.admin.coachingV2.reviews.removeAttachment.useMutation()
  const publishComments =
    trpc.admin.coachingV2.reviews.publishComments.useMutation()
  const unpublishComments =
    trpc.admin.coachingV2.reviews.unpublishComments.useMutation()
  const editComment = trpc.admin.coachingV2.reviews.editComment.useMutation()

  const refresh = useCallback(async (): Promise<void> => {
    await Promise.all([
      // Both halves of the tab: a lifecycle command moves a row's status, and
      // that moves its week's `awaitingCount` in the header above it.
      utils.admin.coachingV2.reviews.listWeeks.invalidate(),
      utils.admin.coachingV2.reviews.listWeekWork.invalidate(),
      utils.admin.coachingV2.reviews.listComments.invalidate(),
      // Every write funnels through `run`, which refreshes afterwards — so
      // this is what makes an attach or a detach show up, and what re-reads
      // the truth after one fails.
      utils.admin.coachingV2.reviews.listAttachments.invalidate(),
      // The stat strip's feedback mix and the shell's review badge are
      // server-computed off the same comments and reviews this just moved.
      utils.admin.coachingV2.overview.get.invalidate({ clientId }),
    ])
  }, [clientId, utils])

  return {
    clientId,
    engagementId: overviewQuery.data?.engagement.id ?? null,
    weeks: {
      data: weeksQuery.data?.weeks,
      isLoading: weeksQuery.isLoading || overviewQuery.isLoading,
      // Each read suppressed against its *own* cache before folding: cached
      // weeks must not silence an overview read that never answered — that
      // failure is what keeps `engagementId` null, and hiding it turns every
      // write into an eternal "still loading" refusal.
      error: surfacedError(weeksQuery) ?? surfacedError(overviewQuery),
    },
    useWeekWork,
    useComments,
    useApproaches,
    useAttachments,
    claim: claim.mutateAsync,
    finish: finish.mutateAsync,
    reopen: reopen.mutateAsync,
    pullIntoWeek: pullIntoWeek.mutateAsync,
    createComment: createComment.mutateAsync,
    approveComment: approveComment.mutateAsync,
    dismissComment: dismissComment.mutateAsync,
    postComment: postComment.mutateAsync,
    addAttachment: addAttachment.mutateAsync,
    removeAttachment: removeAttachment.mutateAsync,
    publishComments: publishComments.mutateAsync,
    unpublishComments: wireUnpublish(unpublishComments.mutateAsync),
    editComment: editComment.mutateAsync,
    refresh,
  }
}

function loadError(...errors: unknown[]): string | null {
  const failure = errors.find((error) => error != null)
  if (failure === undefined) return null
  return errorReason(failure) ?? 'The review queue could not be loaded.'
}

function randomUuid(): string {
  return globalThis.crypto.randomUUID()
}

/**
 * A local body edit plus the *resolved* current text it was made against —
 * `commentBodyResolver`, not the raw `body` column, because publishing an
 * edit moves `currentRevisionId` while deliberately leaving `body` unchanged
 * (LG-529). If the current text moves underneath the draft — a rewrite
 * published elsewhere, another coach editing the same comment — the draft is
 * abandoned rather than left masking the newer wording.
 */
interface DraftBody {
  storedBody: string
  text: string
}

function withoutDraft(
  drafts: Record<string, DraftBody>,
  id: string,
): Record<string, DraftBody> {
  if (!(id in drafts)) return drafts
  const { [id]: _dropped, ...rest } = drafts
  return rest
}

export function useReviewSlice(api: ReviewPersistenceApi): ReviewSlice {
  // Unsaved textarea edits, layered over the stored bodies. `editBody` fires
  // per keystroke, so it stays local until the coach asks for it to be saved
  // (`saveEdit`, which writes a revision and publishes it); an unsaved edit is
  // dropped as soon as the comment it shadows moves — never left on screen
  // impersonating what the client will read.
  const [drafts, setDrafts] = useState<Record<string, DraftBody>>({})

  // The spine, and which of its sections are open. Read first because the row
  // reads follow the folds: a week's work is fetched when the coach opens it,
  // so nothing below this can exist until the weeks do.
  const weekRows = api.weeks.data ?? EMPTY_WEEK_ROWS
  const planWeeks = useMemo<ReviewPlanWeek[]>(
    () => weekRows.map(toReviewPlanWeek),
    [weekRows],
  )
  // The newest week is the one a coach came for, so it is the one that opens
  // by default — and, being the only one open, the only one whose rows load.
  // `useWeekFolds` picks it, because it also has to notice when that week
  // stops existing.
  const folds = useWeekFolds(planWeeks)
  const { isWeekCollapsed } = folds
  const openWeekIds = useMemo(
    () =>
      planWeeks
        .filter((week) => !isWeekCollapsed(week.id))
        .map((week) => week.id),
    [isWeekCollapsed, planWeeks],
  )
  const workByWeek = api.useWeekWork(openWeekIds)

  // Rows across every open week, newest week first and oldest row first inside
  // each — the order the sections render in, so `queue[0]` is the row the
  // accordion falls back to.
  const queueItems = useMemo(
    () =>
      openWeekIds.flatMap((weekId) => workByWeek[weekId]?.data?.items ?? []),
    [openWeekIds, workByWeek],
  )
  // The review-backed rows, which is what everything comment- and
  // lifecycle-shaped below is defined over. A practice session has no review:
  // nothing to fan comments out for, nothing to claim, finish or version.
  const reviewItems = useMemo(
    () =>
      queueItems.filter(
        (item): item is ReviewQueueItem => item.rowKind === 'review',
      ),
    [queueItems],
  )
  const reviewIds = useStableArray(reviewItems.map((item) => item.reviewId))
  const commentsQuery = api.useComments(reviewIds)
  const commentPages = commentsQuery.data ?? EMPTY_PAGES

  // Optimistic-concurrency lookups: every write sends the version it read.
  const commentRows = useMemo(() => {
    const rows = new Map<string, ReviewCommentsPage['comments'][number]>()
    for (const page of Object.values(commentPages)) {
      if (page === undefined) continue
      for (const row of page.comments) rows.set(row.id, row)
    }
    return rows
  }, [commentPages])

  // Each loaded comment's *current* text, resolved through its published
  // revision (`commentBodyResolver` — the one rule of record). The draft
  // baseline below keys off this, never the raw `body` column: publishing an
  // edit moves `currentRevisionId` and leaves `body` alone, and a raw-column
  // baseline would keep a draft alive across exactly that move.
  const currentBodies = useMemo(() => {
    const bodies = new Map<string, string>()
    for (const page of Object.values(commentPages)) {
      if (page === undefined) continue
      const currentBody = commentBodyResolver(page.revisions)
      for (const row of page.comments) bodies.set(row.id, currentBody(row))
    }
    return bodies
  }, [commentPages])

  // A draft whose comment's current text moved is *pruned from state*, not
  // merely hidden at render time: a retained entry would match its old
  // baseline again if a later refresh restored the prior wording — the
  // abandoned draft resurrecting over the published body — and would keep
  // `id in drafts` claiming a draft the UI already treated as dropped (so
  // the next edit would silently skip the not-saved notice). A draft whose
  // comment page has not loaded (no entry in `currentBodies`) is kept: its
  // baseline has not moved, its page is merely in flight. Actively-edited
  // drafts are untouched unless their baseline moved — the slice's contract
  // is "dropped as soon as the comment it shadows moves", nothing sooner.
  useEffect(() => {
    setDrafts((current) => {
      const stale = Object.entries(current).filter(([id, draft]) => {
        const body = currentBodies.get(id)
        return body !== undefined && body !== draft.storedBody
      })
      if (stale.length === 0) return current
      const next = { ...current }
      for (const [id] of stale) delete next[id]
      return next
    })
  }, [currentBodies])

  // Drafts still standing: the ones whose comment is loaded and whose current
  // text has not moved since the coach typed over it. The same predicate the
  // pruning effect applies — kept at render time too so a stale draft never
  // shows for the one commit before the effect prunes it.
  const draftBodies = useMemo(() => {
    const bodies: Record<string, string> = {}
    for (const [id, draft] of Object.entries(drafts)) {
      if (currentBodies.get(id) === draft.storedBody) bodies[id] = draft.text
    }
    return bodies
  }, [currentBodies, drafts])

  const commentsByReviewId = useMemo(() => {
    const byReview = new Map<string, ReviewComment[]>()
    for (const item of reviewItems) {
      const page = commentPages[item.reviewId]
      byReview.set(
        item.reviewId,
        page === undefined
          ? EMPTY_COMMENTS
          : toReviewComments(
              page.comments,
              page.revisions,
              draftBodies,
              item.kind,
            ),
      )
    }
    return byReview
  }, [commentPages, draftBodies, reviewItems])

  const queue = useMemo(() => {
    const now = new Date()
    return queueItems.map((item) => {
      // A session nobody submitted renders read-only: no comments to load, no
      // review to finish, so none of the policy below applies to it.
      if (item.rowKind === 'session') return toPracticeRow(item, now)
      const submission = toReviewSubmission(
        item,
        commentsByReviewId.get(item.reviewId) ?? EMPTY_COMMENTS,
        now,
      )
      // Policy, not mapping, so it lives here and not in the mapper: a page
      // still missing from the fan-out means the row's comments are not on
      // screen yet, and after that a seed-bodied kind still isn't showing the
      // coach this client's work. A coding row that clears both holds carries
      // no key at all, rather than a reason that no longer exists.
      const reason =
        commentPages[item.reviewId] === undefined
          ? COMMENTS_PENDING_REASON
          : rendersRealArtifact(item.kind)
            ? null
            : SEED_ARTIFACT_REASON
      return reason === null
        ? submission
        : { ...submission, finishDisabledReason: reason }
    })
  }, [commentPages, commentsByReviewId, queueItems])

  const comments = useMemo(
    () => queue.flatMap((submission) => submission.comments),
    [queue],
  )

  const reviewRows = useMemo(() => {
    const rows = new Map<string, ReviewQueueItem>()
    for (const item of reviewItems) rows.set(item.reviewId, item)
    return rows
  }, [reviewItems])

  // Which week section each loaded submission sits in, so the accordion can
  // drop a selection whose section the coach has folded (`useReviewUiState`).
  // Built from the read that produced the rows rather than from a field on the
  // row: a row is in the week whose query returned it, by construction.
  const weekKeys = useMemo(() => {
    const keys = new Map<string, string>()
    for (const weekId of openWeekIds) {
      for (const item of workByWeek[weekId]?.data?.items ?? EMPTY_ITEMS) {
        keys.set(
          item.rowKind === 'review' ? item.reviewId : item.sessionId,
          weekId,
        )
      }
    }
    return keys
  }, [openWeekIds, workByWeek])
  // Null for a submission the tab does not currently hold: unknown is not "in
  // some folded week", and it must not be hidden as if it were.
  const weekKeyOf = useCallback(
    (submissionId: string): string | null => weekKeys.get(submissionId) ?? null,
    [weekKeys],
  )

  // The sections themselves. A week with no rows and nothing in flight is
  // genuinely quiet — that is the state this whole change exists to make
  // visible — so the three cases stay distinguishable rather than collapsing
  // into one empty list.
  const weeks = useMemo<ReviewWeekGroup[]>(() => {
    const byWeek = new Map<string, ReviewSubmission[]>()
    for (const submission of queue) {
      const weekId = weekKeys.get(submission.id)
      if (weekId === undefined) continue
      const bucket = byWeek.get(weekId)
      if (bucket === undefined) byWeek.set(weekId, [submission])
      else bucket.push(submission)
    }
    return planWeeks.map((week) => {
      const state = workByWeek[week.id]
      // Suppressed against this week's own cache (`surfacedError`, the same
      // rule `approachesError` and `attachmentsError` apply below): React
      // Query keeps the last good rows through a failed *background* refetch,
      // and an alert saying the week could not be loaded directly above that
      // week's rendered rows is a worse lie than a silent retry.
      const weekError = state === undefined ? null : surfacedError(state)
      return {
        week,
        submissions: sortByWorkedAt(byWeek.get(week.id) ?? EMPTY_SUBMISSIONS),
        isLoading: state?.isLoading === true,
        error:
          weekError === null
            ? null
            : (errorReason(weekError) ??
              'This week’s work could not be loaded.'),
        isTruncated: state?.data?.truncated === true,
      }
    })
  }, [planWeeks, queue, weekKeys, workByWeek])

  const ui = useReviewUiState(queue[0]?.id ?? null, weekKeyOf, isWeekCollapsed)
  const { expandedId } = ui

  // Scoped to the expanded card — see `useApproaches`'s note on why this read
  // is the one that takes the review id as an argument. A failure here leaves
  // the Alternative Approach section unrendered rather than blanking the
  // queue, so it deliberately stays out of `loadError` below.
  //
  // Narrowed again to *coding* cards: `CodingReview` is the only body that
  // renders the section, so expanding a behavioral or system-design card would
  // otherwise spend a five-table join on a result nothing reads.
  // …and narrowed once more past that to rows with a review behind them: a
  // practice session's card renders no approach picker and no attach state,
  // and both reads are keyed by a review id it does not have.
  const expandedRow = queue.find((item) => item.id === expandedId)
  const expandedApproachesId =
    expandedRow?.type === 'coding' && expandedRow.notSubmitted !== true
      ? expandedId
      : null
  const approaches = api.useApproaches(expandedApproachesId)
  const expandedApproaches = approaches.data ?? EMPTY_APPROACHES
  const approachesFor = useCallback(
    (submissionId: string): ReviewApproach[] =>
      submissionId === expandedId ? expandedApproaches : EMPTY_APPROACHES,
    [expandedApproaches, expandedId],
  )
  // Suppressed against its own cache (`surfacedError`): React Query keeps
  // the last successful `data` through a failed *background* refetch (window
  // refocus, reconnect), and a working picker sitting under "could not be
  // loaded" is a worse lie than the silent retry.
  const approachesSurfaced = surfacedError(approaches)
  const approachesError =
    approachesSurfaced === null
      ? null
      : (errorReason(approachesSurfaced) ??
        'This problem’s alternative approaches could not be loaded.')
  const approachesErrorFor = useCallback(
    (submissionId: string): string | null =>
      submissionId === expandedId ? approachesError : null,
    [approachesError, expandedId],
  )

  // Gated on exactly the same id as the approaches read: the attach state is
  // only meaningful beside the picker it belongs to, and the two must agree
  // about which card they describe or a coach would see one card's toggles
  // over another's approaches.
  const attachments = api.useAttachments(expandedApproachesId)
  const expandedAttachments = attachments.data ?? EMPTY_ATTACHMENTS
  // "No answer yet" is a third state, kept distinct from "nothing attached".
  // Folded together they make the UI assert the opposite of the truth: every
  // chip loses its tick, the toggle reads "Attach to review" with
  // `aria-pressed="false"` for approaches that *are* attached, and the click
  // that follows takes the attach branch on a pair that already has a live
  // row. Same reasoning as `approachesError` above, one axis over -- there the
  // cost is a wrong label, here it also steers a write.
  const attachmentsKnown = attachments.data !== undefined
  const attachmentsSurfaced = surfacedError(attachments)
  const attachmentsError =
    attachmentsSurfaced === null
      ? null
      : (errorReason(attachmentsSurfaced) ??
        'The approaches attached to this review could not be loaded.')
  // approachId → attachment id, over the *approach* attachments only. The
  // read returns every live attachment on the review, and the other three
  // kinds all leave `approachId` null; keying off the column rather than off
  // `kind` is what keeps a future fifth kind from landing under `null` here.
  const attachedApproaches = useMemo(() => {
    const byApproachId = new Map<string, string>()
    for (const attachment of expandedAttachments) {
      if (attachment.approachId !== null) {
        byApproachId.set(attachment.approachId, attachment.id)
      }
    }
    return byApproachId
  }, [expandedAttachments])
  const attachedApproachIds = useMemo(
    () => new Set(attachedApproaches.keys()),
    [attachedApproaches],
  )
  // Null rather than an empty set when the read has not answered: the section
  // renders "attached" and "not attached" differently, and it must be able to
  // render "not known" as neither.
  const attachedApproachIdsFor = useCallback(
    (submissionId: string): ReadonlySet<string> | null => {
      if (submissionId !== expandedId) return NO_ATTACHED_APPROACHES
      return attachmentsKnown ? attachedApproachIds : null
    },
    [attachedApproachIds, attachmentsKnown, expandedId],
  )
  const attachmentsErrorFor = useCallback(
    (submissionId: string): string | null =>
      submissionId === expandedId ? attachmentsError : null,
    [attachmentsError, expandedId],
  )

  // Destructured, not read off `api` inside the callbacks: the tRPC api hook
  // returns a fresh object every render (every query tick, every keystroke),
  // so depending on it would rebuild every command — and with them the slice
  // and the context value the whole tab re-renders from. The commands
  // themselves are stable `mutateAsync` bindings.
  const {
    clientId,
    engagementId,
    refresh,
    approveComment,
    dismissComment,
    createComment,
    postComment: postCommentApi,
    publishComments,
    unpublishComments,
    editComment,
    claim: claimReview,
    finish: finishReview,
    reopen: reopenReview,
    pullIntoWeek,
    addAttachment: addAttachmentApi,
    removeAttachment: removeAttachmentApi,
  } = api
  // Answers with what the command returned, `undefined` when it failed — the
  // failure is already a toast, so a caller that has nothing to read simply
  // ignores it and a caller that does can tell the two apart.
  const run = useCallback(
    async <T,>(
      label: string,
      command: () => Promise<T>,
    ): Promise<T | undefined> => {
      let answer: T | undefined
      try {
        answer = await command()
      } catch (error) {
        const reason = errorReason(error)
        toast.error(
          reason === null ? `${label} failed.` : `${label} — ${reason}`,
        )
      }
      // Also after a failure: a rejected version check means what is on screen
      // is behind, and the refreshed rows are what the next attempt needs.
      //
      // Caught on its own account, because the write above has already been
      // reported and every caller here documents itself as never rejecting: a
      // re-read that fails on top of a command must not turn a settled
      // lifecycle call into a rejected promise, or `await publish(…)` starts
      // throwing at a click handler that has nothing left to do about it. The
      // rows stay stale either way, so the coach is told that much.
      try {
        await refresh()
      } catch {
        toast.error('The queue could not be refreshed — it may be out of date.')
      }
      return answer
    },
    [refresh],
  )

  // The engagement is resolved by a *separate* query, so the queue can be on
  // screen while it is still missing. Every write goes through here so that
  // case says so instead of turning a click into nothing.
  const writeScope = useCallback((): {
    clientId: string
    engagementId: string
  } | null => {
    if (engagementId !== null) return { clientId, engagementId }
    toast.error(
      'This client’s engagement has not loaded yet — try again in a moment.',
    )
    return null
  }, [clientId, engagementId])

  const approve = useCallback(
    (id: string) => {
      const row = commentRows.get(id)
      const scope = writeScope()
      if (row === undefined || scope === null) return
      // The command carries the stored body, so the draft goes: leaving it up
      // would show the coach their own wording where the client sees another.
      setDrafts((current) => withoutDraft(current, id))
      void run('Approving the comment', () =>
        approveComment({
          ...scope,
          commentId: id,
          commentVersion: row.version,
        }),
      )
    },
    [approveComment, commentRows, run, writeScope],
  )

  const dismiss = useCallback(
    (id: string) => {
      const row = commentRows.get(id)
      const scope = writeScope()
      if (row === undefined || scope === null) return
      setDrafts((current) => withoutDraft(current, id))
      void run('Dismissing the comment', () =>
        dismissComment({
          ...scope,
          commentId: id,
          commentVersion: row.version,
        }),
      )
    },
    [commentRows, dismissComment, run, writeScope],
  )

  const markPosted = useCallback(
    (id: string) => {
      const row = commentRows.get(id)
      const scope = writeScope()
      if (row === undefined || scope === null) return
      // Posting carries the stored body, so a local draft goes the same way
      // it does on approve/dismiss.
      setDrafts((current) => withoutDraft(current, id))
      void run('Posting the comment to the client', () =>
        postCommentApi({
          ...scope,
          commentId: id,
          commentVersion: row.version,
        }),
      )
    },
    [commentRows, postCommentApi, run, writeScope],
  )

  const post = useCallback(
    (comment: Omit<ReviewComment, 'id' | 'status' | 'source'>) => {
      const scope = writeScope()
      if (scope === null || expandedId === null) return
      void run('Posting the comment', () =>
        createComment({
          ...scope,
          reviewId: expandedId,
          source: 'coach',
          kind: comment.kind,
          sectionKey: comment.section,
          // An explicit anchor wins over one derived from the label: the
          // post-mortem's targets are live question wording, which is not
          // identity (LG-765). Sections with fixed labels pass none and keep
          // deriving.
          targetKey:
            comment.targetKey ?? toTargetKey(comment.section, comment.target),
          targetLabel: comment.target,
          body: comment.body,
          lineStart: comment.lines?.[0] ?? null,
          lineEnd: comment.lines?.[1] ?? null,
          diffBefore: comment.diff?.before ?? null,
          diffAfter: comment.diff?.after ?? null,
          aiProvider: null,
          aiModel: null,
          aiRunId: null,
          aiPromptVersion: null,
          aiGeneratedAt: null,
        }),
      )
    },
    [createComment, expandedId, run, writeScope],
  )

  const publish = useCallback(
    async (reviewId: string): Promise<void> => {
      const scope = writeScope()
      if (scope === null) return
      await run('Publishing the feedback', () =>
        publishComments({ ...scope, reviewId }),
      )
    },
    [publishComments, run, writeScope],
  )

  const unpublish = useCallback(
    async (
      reviewId: string,
      confirm: (clientLastViewedWhen: string | null | undefined) => boolean,
    ): Promise<void> => {
      const scope = writeScope()
      if (scope === null) return
      // The page's own reading opens the conversation; the service has the
      // last word on it. `undefined` — no page loaded — acknowledges nothing,
      // which the service answers with whatever it can see (LG-641).
      let acknowledged: string | null | undefined =
        commentPages[reviewId]?.clientLastViewedAt
      // Two asks, not a loop: the second one states what the service saw, and
      // a client who reads the feedback again in that window has changed the
      // answer often enough that saying so beats another dialog.
      for (let ask = 0; ask < 2; ask += 1) {
        const when =
          acknowledged === null || acknowledged === undefined
            ? acknowledged
            : formatRelativeWhen(acknowledged, new Date())
        if (!confirm(when)) return
        const outcome = await run('Unpublishing the feedback', () =>
          unpublishComments({
            ...scope,
            reviewId,
            acknowledgedClientViewedAt: acknowledged ?? null,
          }),
        )
        if (outcome === undefined || outcome.status === 'unpublished') return
        acknowledged = outcome.clientLastViewedAt
      }
      toast.error(
        'The client opened this feedback again — nothing was unpublished.',
      )
    },
    [commentPages, run, unpublishComments, writeScope],
  )

  const claim = useCallback(
    async (reviewId: string): Promise<void> => {
      const item = reviewRows.get(reviewId)
      const scope = writeScope()
      if (item === undefined || scope === null) return
      await run('Claiming the review', () =>
        claimReview({
          ...scope,
          reviewId,
          reviewVersion: item.reviewVersion,
        }),
      )
    },
    [claimReview, reviewRows, run, writeScope],
  )

  const finish = useCallback(
    async (reviewId: string): Promise<void> => {
      const item = reviewRows.get(reviewId)
      const scope = writeScope()
      if (item === undefined || scope === null) return
      await run('Finishing the review', () =>
        finishReview({
          ...scope,
          reviewId,
          reviewVersion: item.reviewVersion,
          assignmentId: item.assignmentId,
          assignmentVersion: item.assignmentVersion,
          commandId: randomUuid(),
        }),
      )
    },
    [finishReview, reviewRows, run, writeScope],
  )

  const reopen = useCallback(
    async (reviewId: string): Promise<void> => {
      const item = reviewRows.get(reviewId)
      const scope = writeScope()
      if (item === undefined || scope === null) return
      await run('Reopening the review', () =>
        reopenReview({
          ...scope,
          reviewId,
          reviewVersion: item.reviewVersion,
          assignmentId: item.assignmentId,
          assignmentVersion: item.assignmentVersion,
          commandId: randomUuid(),
        }),
      )
    },
    [reopenReview, reviewRows, run, writeScope],
  )

  const pull = useCallback(
    async (sessionId: string): Promise<void> => {
      const scope = writeScope()
      // The week the row was read in — the pull's whole point is that the
      // work already happened, so the week is a fact about it rather than a
      // choice, and the service refuses any other. A row whose week the tab
      // does not currently hold cannot be pulled: there is nothing to name.
      const weekId = weekKeyOf(sessionId)
      if (scope === null || weekId === null) return
      const answer = await run('Pulling this practice into the week', () =>
        pullIntoWeek({ ...scope, weekId, sessionId }),
      )
      // The row the coach was reading is gone: a practice row is keyed by its
      // session and the review that replaces it is keyed by its review id, so
      // the open card would collapse and nothing would take its place. Every
      // other lifecycle command keeps the row's identity; this one is the only
      // one that has to hand the accordion the new one. Both arms can answer
      // "nothing to open" — a fresh pull whose work qualified against nothing,
      // or a replay whose submission has no live review — and neither is a
      // reason to move it.
      const reviewId =
        answer === undefined
          ? null
          : answer.replayed
            ? answer.reviewId
            : (answer.qualified?.reviewId ?? null)
      if (reviewId !== null) ui.setExpandedId(reviewId)
    },
    [pullIntoWeek, run, ui.setExpandedId, weekKeyOf, writeScope],
  )

  const draft = useCallback(
    (id: string, text: string) => {
      const storedBody = currentBodies.get(id)
      if (storedBody === undefined) return
      setDrafts((current) => ({
        ...current,
        [id]: { storedBody, text },
      }))
    },
    [currentBodies],
  )

  const saveEdit = useCallback(
    async (id: string): Promise<void> => {
      const row = commentRows.get(id)
      const scope = writeScope()
      const body = draftBodies[id]
      if (row === undefined || scope === null || body === undefined) return
      // The same predicate the service checks and the card gates its button
      // on: a pending AI draft has to be approved or dismissed first, so this
      // is not a write to attempt and be told off for.
      if (!isSavableComment(row)) return
      if (body.trim() === '') {
        toast.error('An empty comment cannot be saved.')
        return
      }
      // One call: the service writes the revision and publishes it in the same
      // transaction, so a failure leaves no half-saved edit behind. The local
      // draft stays up throughout and is dropped by the pruning effect once
      // the refresh brings back the new stored text.
      //
      // So a failure keeps the coach's wording on screen only while the stored
      // body has not moved — a network or validation failure, which is most of
      // them. A `CONFLICT` raised *because* somebody else edited this comment
      // is the other case: the refresh brings back their text, the baseline
      // this draft shadows is gone, and the pruning effect drops it by the
      // slice's standing rule. That is deliberate — a retained draft would sit
      // over a body it was never written against, and saving it again would
      // overwrite the other edit — but it does mean a conflicting save is the
      // one failure that costs the coach what they typed.
      await run('Saving the comment', () =>
        editComment({
          ...scope,
          commentId: id,
          commentVersion: row.version,
          body,
          diffBefore: row.diffBefore,
          diffAfter: row.diffAfter,
          revisionSource: 'coach_edit',
          rewriteInstruction: null,
          publishRevision: true,
          aiProvider: null,
          aiModel: null,
          aiRunId: null,
          aiPromptVersion: null,
          aiGeneratedAt: null,
        }),
      )
    },
    [commentRows, draftBodies, editComment, run, writeScope],
  )

  const rewrite = useCallback(
    (id: string) => {
      const alt = comments.find((comment) => comment.id === id)?.alt
      if (alt !== undefined) draft(id, alt)
    },
    [comments, draft],
  )

  const editBody = useCallback(
    (id: string, body: string) => draft(id, body),
    [draft],
  )

  const toggleApproachAttachment = useCallback(
    async (submissionId: string, approach: ReviewApproach): Promise<void> => {
      const scope = writeScope()
      // Only the card whose attachments were actually read can be toggled.
      // Without this an approach on some other card would be judged against
      // an empty map and always take the attach branch, writing a duplicate
      // the unique index would then refuse.
      if (scope === null || submissionId !== expandedId) return
      // And only once the read has answered. Judged against an empty map an
      // already-attached approach takes the attach branch, so a failed read
      // would turn every click into a duplicate write.
      if (!attachmentsKnown) return
      const attachmentId = attachedApproaches.get(approach.approachId)
      if (attachmentId !== undefined) {
        await run('Detaching the approach', () =>
          removeAttachmentApi({ ...scope, attachmentId }),
        )
        return
      }
      await run('Attaching the approach', () =>
        addAttachmentApi({
          ...scope,
          reviewId: submissionId,
          kind: 'approach',
          approachId: approach.approachId,
          learningItemId: null,
          sourceAssignmentId: null,
          // The catalog name as it read when the coach attached it. The row
          // it points at can be renamed or retired afterwards, and what the
          // coach chose is a fact about that moment.
          titleSnapshot: approach.name,
          urlSnapshot: null,
          payloadSchemaVersion: null,
          payloadSnapshot: null,
        }),
      )
    },
    [
      addAttachmentApi,
      attachedApproaches,
      attachmentsKnown,
      expandedId,
      removeAttachmentApi,
      run,
      writeScope,
    ],
  )

  const commentsFor = useCallback(
    (submissionId: string): ReviewComment[] =>
      (
        queue.find((submission) => submission.id === submissionId)?.comments ??
        EMPTY_COMMENTS
      ).filter((comment) => comment.status !== 'dismissed'),
    [queue],
  )

  // The spine, and only the spine: without it there are no sections to render
  // at all, whereas everything read *beneath* it belongs to one week and is
  // reported on that week's own section (`weeks[].isLoading`, `weeks[].error`).
  //
  // Folding the comment fan-out in here is what that rule exists to prevent.
  // Comments are re-read whenever a week is expanded, so a tab-wide flag would
  // print "Loading the review weeks…" above week headers that are already on
  // screen — beside the correct per-week spinner underneath — and one row's
  // failed `listComments` would paint "The review queue could not be loaded."
  // over a queue that loaded fine.
  // Surfaced as handed over: `api.weeks.error` folds more than one read, and
  // the api already suppressed each against its own cache (`surfacedError`),
  // so an error arriving here has no data anywhere to contradict it. A
  // data-is-on-screen check at this end would re-suppress by the *weeks*
  // cache alone and swallow an overview read that never answered.
  const isLoading = api.weeks.isLoading
  const error = loadError(api.weeks.error)

  // Memoized because it is a context value: `CoachingReviewProvider` holds it
  // for the whole Review tab, so a fresh literal per render would re-render
  // every queue row, comment card and section on every query tick.
  return useMemo(
    () => ({
      // Counters over the comments this slice actually holds — the rows of the
      // weeks the coach currently has open, since rows are read a week at a
      // time. The sprint-week comparison the stat strip renders is
      // server-computed by `overview.get`, which windows every comment in the
      // engagement; the review lane holds no last-week data of its own and
      // says so rather than repeating the current numbers.
      feedbackMix: {
        thisWeek: countByKind(comments),
        lastWeek: emptyKindCounts(),
      },
      byQuestionType: {
        thisWeek: countByQuestionType(queue),
        lastWeek: emptyTypeCounts(),
      },
      // Summed over the *spine*, not over the loaded rows: the server counts
      // each week's open reviews whether or not the coach has opened it, so
      // the badge stays the client's true outstanding total rather than
      // whatever happens to be on screen.
      awaitingCount: planWeeks.reduce(
        (total, week) => total + week.awaitingCount,
        0,
      ),

      weeks,
      queue,
      expandedId,
      setExpandedId: ui.setExpandedId,
      isWeekCollapsed,
      toggleWeekCollapsed: folds.toggleWeekCollapsed,

      comments,
      commentsFor,
      approachesFor,
      approachesErrorFor,
      attachedApproachIdsFor,
      attachmentsErrorFor,
      toggleApproachAttachment,
      approve,
      dismiss,
      rewrite,
      editBody,
      saveEdit,
      post,
      markPosted,

      claim,
      finish,
      reopen,
      pull,
      publish,
      unpublish,

      composer: ui.composer,
      openComposer: ui.openComposer,
      openLineComposer: ui.openLineComposer,
      closeComposer: ui.closeComposer,
      extendComposerLines: ui.extendComposerLines,
      setComposerKind: ui.setComposerKind,
      setComposerDraft: ui.setComposerDraft,
      overallDrafts: ui.overallDrafts,
      setOverallDraft: ui.setOverallDraft,

      attachments: ui.attachments,
      toggleAttachment: ui.toggleAttachment,

      status: { isLoading, error },
    }),
    [
      approachesErrorFor,
      approachesFor,
      approve,
      attachedApproachIdsFor,
      attachmentsErrorFor,
      claim,
      comments,
      commentsFor,
      dismiss,
      editBody,
      error,
      expandedId,
      finish,
      isLoading,
      markPosted,
      folds,
      isWeekCollapsed,
      planWeeks,
      post,
      publish,
      pull,
      queue,
      reopen,
      weeks,
      rewrite,
      toggleApproachAttachment,
      saveEdit,
      ui,
      unpublish,
    ],
  )
}
